import { MonitoringGoodsDetail } from '@/types/api';
import { LayoutStickySection } from '../orders/_layouts/LayoutOrderContainer';
import DeliveryIcon from '@/assets/icons/Monitoring/IconTagDelivery.svg?react';
import { ImageIcon } from 'lucide-react';

import { useState, useCallback } from 'react';
import { useMonitoringGoods } from './MonitoringContext';
import { usePost } from '@/api';
import { CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/V3/useToast';
import DeleteGoodsBtn from './DeleteGoodsBtn';

interface GoodsDetailStickyProps {
  goods: MonitoringGoodsDetail;
}

const GoodsDetailSticky = ({ goods }: GoodsDetailStickyProps) => {
  const user = useAppSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState(false);

  const { refetch } = useMonitoringGoods();
  const { showToast } = useToast();

  // 날짜 포맷 (YYYY.MM.DD)
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return dateStr.slice(0, 10).replace(/-/g, '.');
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { mutate: setMonitoringGoods } = usePost(CPLAT_API_ROUTES.setUserMonitoringGoods);

  const handleDelete = () => {
    setIsLoading(true);
    setMonitoringGoods(
      {
        cplatToken: user.CplatToken,
        GoodsNo: goods.GoodsNo,
        NoticeYn: goods.NoticeYn,
        StoreName: goods.StoreName,
        UseYn: 'N',
        InsertYn: 'N',
      },
      {
        onSuccess: (res) => {
          const { code } = res.data;

          if (code === '200') {
            showToast('success', '상품이 삭제되었습니다.');
            refetch();
            const params = new URLSearchParams(searchParams);
            params.delete('select');
            navigate({ search: params.toString() }, { replace: true });
          } else {
            showToast('warning', res.data.message || '상품 삭제에 실패했습니다.');
          }
          setIsLoading(false);
        },
        onError: () => {
          showToast('warning', '상품 삭제에 실패했습니다. 다시 시도해주세요.');
        },
        onSettled: () => {
          setIsLoading(false);
        },
      },
    );
  };

  return (
    <LayoutStickySection className="max-w-full bg-gr-50 py-[10px]">
      <div className="flex max-w-full rounded-[12px]   bg-white p-4">
        <div className="flex w-full flex-1 gap-3">
          {/* 상품 이미지 */}
          <GoodsDetailImage src={goods.GoodsImageUrl} alt={goods.GoodsName} />
          <div className="flex min-w-0 flex-1 flex-col gap-[10px]">
            {/* 상품 정보 */}
            <div className="flex min-w-0  flex-col">
              <div className="flex flex-col gap-[6px]">
                {/* 상품명 */}
                <div className="text-gr-800 Body1S16">{goods.GoodsName}</div>
                <div className="flex flex-col gap-[2px] text-gr-600 Caption1M12">
                  {/* 카테고리 */}
                  <div className="">{goods.CategoryFullName}</div>
                  {/* 등록일 */}
                  <div className="">제품등록일 : {formatDate(goods.GoodsRegisterDate)}</div>
                </div>
              </div>
            </div>

            {/* 가격/배송비 */}
            <div className="flex min-w-[100px] items-center gap-3">
              <div className="text-gr-800 Body1S16">{goods.GoodsPrice?.toLocaleString()}원</div>
              <div className="flex items-center gap-[8px]">
                <div className="flex h-[20px] w-[20px] rounded-full bg-[#F3F3F4] p-[3px]">
                  <DeliveryIcon />
                </div>
                <div className="text-gr-800 Body5M14">{goods.ShippingFee?.toLocaleString()}원</div>
              </div>
            </div>
          </div>
        </div>
        <DeleteGoodsBtn handleDelete={handleDelete} isLoading={isLoading} />
      </div>
    </LayoutStickySection>
  );
};

export default GoodsDetailSticky;

const GoodsDetailImage = ({ src, alt }: { src?: string; alt?: string }) => {
  const [hasError, setHasError] = useState(!src);
  const handleError = useCallback(() => setHasError(true), []);

  if (hasError) {
    return (
      <div className="flex h-[120px] w-[120px] shrink-0 items-center justify-center rounded-md border border-gr-200 bg-gr-50">
        <ImageIcon className="h-10 w-10 text-gr-300" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={handleError}
      className="h-[120px] w-[120px] shrink-0 rounded-md border border-gr-200 bg-white object-cover"
    />
  );
};
